import { TrashIcon } from '@phosphor-icons/react'
import { ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Service, ServiceSku, useDeleteService, useDeleteSku } from '@/api/services'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { popupFactory, toastFactory } from '@/factories'
import { getApiErrorMessage } from '@/utils/error'

export const useServicesDelete = () => {
  const { t } = useTranslation(['organisation', 'common'])
  const { mutateAsync: deleteService } = useDeleteService()
  const { mutateAsync: deleteSku } = useDeleteSku()

  const confirmDelete = ({
    body,
    delete: remove,
    deletedMessage,
    title,
  }: {
    body: ReactNode
    delete: () => Promise<unknown>
    deletedMessage: string
    title: string
  }) =>
    popupFactory.open(ConfirmationPopup, {
      children: body,
      confirmIcon: TrashIcon,
      confirmText: t('common:actions.delete'),
      onConfirm: async () => {
        try {
          await remove()
          toastFactory.success(deletedMessage)
        } catch (error) {
          toastFactory.error(getApiErrorMessage(error))
        }
      },
      title,
      variant: 'danger',
    })

  const handleDeleteService = (service: Service) =>
    confirmDelete({
      body: (
        <>
          <Trans
            components={[<strong key="name" />]}
            i18nKey="organisation:servicesPage.service.deleteConfirm.body"
            t={t}
            values={{ name: service.name }}
          />
          {service.sku.length > 0 && (
            <>
              <br />
              <br />
              {t('organisation:servicesPage.service.deleteConfirm.skusWarning', {
                count: service.sku.length,
              })}
            </>
          )}
        </>
      ),
      delete: () => deleteService(service.id),
      deletedMessage: t('organisation:servicesPage.service.toasts.deleted'),
      title: t('organisation:servicesPage.service.deleteConfirm.title'),
    })

  const handleDeleteSku = (sku: ServiceSku) =>
    confirmDelete({
      body: (
        <Trans
          components={[<strong key="name" />]}
          i18nKey="organisation:servicesPage.sku.deleteConfirm.body"
          t={t}
          values={{ name: sku.name }}
        />
      ),
      delete: () => deleteSku(sku.id),
      deletedMessage: t('organisation:servicesPage.sku.toasts.deleted'),
      title: t('organisation:servicesPage.sku.deleteConfirm.title'),
    })

  return { handleDeleteService, handleDeleteSku }
}
